import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUp, Check, Copy } from 'lucide-react';
import { company, services } from '../../data/site';
import { legalPages } from '../../data/legal';
import TextHoverEffect from '../fx/TextHoverEffect';
import Button from '../primitives/Button';
import SplitText from '../primitives/SplitText';
import StatusClock from './StatusClock';
import { useScrollTo } from './SmoothScroll';
import { useFooterCta } from '../../context/PageContext';

const companyLinks = [
  { to: '/about', label: 'About' },
  { to: '/blog', label: 'Insights' },
  { to: '/salesforce#testimonials', label: 'Testimonials' },
  { to: '/contact', label: 'Contact' },
  { to: '/contact#careers', label: 'Careers' }
];

const Column = ({ title, children }) => (
  <div>
    <h2 className="label mb-5">{title}</h2>
    <ul className="space-y-3 text-[0.9375rem]">{children}</ul>
  </div>
);

const FooterLink = ({ to, children }) => (
  <li>
    <Link to={to} className="link-draw text-foreground/80 transition-colors hover:text-foreground">{children}</Link>
  </li>
);

// Copies the email address, with a tick for confirmation
const CopyEmail = () => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(company.email);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      window.location.href = `mailto:${company.email}`;
    }
  };
  return (
    <button
      type="button"
      onClick={copy}
      className="inline-flex size-9 items-center justify-center rounded-full border border-border text-muted-foreground transition-colors hover:border-foreground/30 hover:text-foreground"
      aria-label={copied ? 'Email address copied' : 'Copy email address'}
    >
      {copied ? <Check size={15} /> : <Copy size={15} />}
    </button>
  );
};

const Footer = () => {
  const scrollTo = useScrollTo();
  const cta = useFooterCta();
  const year = new Date().getFullYear();

  return (
    <footer data-tone="ink" className="theme-ink relative overflow-hidden bg-background pt-[clamp(5rem,10vw,9rem)]">
      <div className="container-site">
        {/* Closing call to action */}
        <div className="grid gap-10 border-b border-border pb-[clamp(4rem,8vw,7rem)] lg:grid-cols-12">
          <div className="lg:col-span-8">
            <p className="label mb-6">Start a project</p>
            <SplitText key={cta.title} as="h2" text={cta.title} className="display-lg max-w-[16ch]" />
          </div>
          <div className="flex flex-col justify-end gap-6 lg:col-span-4">
            <p className="lede">{cta.text}</p>
            <div className="flex flex-wrap items-center gap-3">
              <Button to="/contact" size="lg" arrow magnetic>{cta.label}</Button>
            </div>
            <div className="flex items-center gap-3 text-[0.9375rem]">
              <a href={`mailto:${company.email}`} className="link-draw font-medium">{company.email}</a>
              <CopyEmail />
            </div>
          </div>
        </div>

        {/* Directory */}
        <div className="grid grid-cols-2 gap-10 py-14 md:grid-cols-4">
          <div className="col-span-2 md:col-span-1">
            <p className="max-w-xs text-sm leading-relaxed text-muted-foreground">{company.description}</p>
          </div>
          <Column title="Services">
            {services.map(s => <FooterLink key={s.slug} to={s.path}>{s.label}</FooterLink>)}
          </Column>
          <Column title="Company">
            {companyLinks.map(l => <FooterLink key={l.to} to={l.to}>{l.label}</FooterLink>)}
          </Column>
          <Column title="Contact">
            <li className="text-foreground/80">{company.address}</li>
            <li><a href={`mailto:${company.email}`} className="link-draw text-foreground/80 hover:text-foreground">{company.email}</a></li>
            <li><a href={company.phoneHref} className="link-draw text-foreground/80 hover:text-foreground">{company.phone}</a></li>
          </Column>
        </div>

        {/* Legal and trust pages: on every page, as Google, Apple and Salesforce reviewers expect */}
        <nav aria-label="Legal" className="border-t border-border py-6">
          <ul className="flex flex-wrap gap-x-6 gap-y-2">
            {legalPages.map(p => (
              <li key={p.slug}>
                <Link to={p.path} className="label transition-colors hover:text-foreground">{p.label}</Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* Status row */}
        <div className="flex flex-wrap items-center justify-between gap-4 border-t border-border py-6">
          <StatusClock />
          <p className="label">// © {year} {company.name}. All rights reserved.</p>
          <button type="button" onClick={() => scrollTo(0)} className="label inline-flex items-center gap-2 transition-colors hover:text-foreground">
            Back to top <ArrowUp size={12} aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* Giant wordmark, cropped by the page edge */}
      <div className="pointer-events-auto -mb-[2.2vw] mt-2 select-none px-[1vw]">
        <TextHoverEffect text="DREAMVISION" viewBox="0 0 1000 150" fontSize={140} />
      </div>
    </footer>
  );
};

export default Footer;
